import DataTable, {Order} from "datatables.net-bs5";
import {number_format} from "./util";

// SortableJS require idiom — copy exactly from src/js/home/sortable.ts (Pitfall 7)
// DO NOT use `import Sortable from 'sortablejs'` — returns undefined (mixed CJS/ESM)
// eslint-disable-next-line @typescript-eslint/no-var-requires
const SortableModule = require('sortablejs');
const Sortable = SortableModule.default || SortableModule;

// Reimbursement presets (D-04) — JS constant; populates the <datalist> on init
const REIMBURSEMENT_PRESETS = ['GitHub Copilot', 'Claude Max', 'PHPStorm'];

// Status badge class mapping (locked — UI-SPEC color contract)
const STATUS_BADGE: Record<string, string> = {
    draft: 'bg-secondary',
    sent:  'bg-primary',
    paid:  'bg-success',
    void:  'bg-dark border border-secondary',
};

const STATUS_BTN: Record<string, string> = {
    draft: 'btn-secondary',
    sent:  'btn-primary',
    paid:  'btn-success',
    void:  'btn-danger',
};

// Timestamp note for status confirm modal (UI-SPEC Copywriting Contract)
function statusTimestampNote(target: string, current: string): string {
    if (target === 'sent') return 'This will record sent_at = now.';
    if (target === 'paid') return 'This will record paid_at = now.';
    if (current === 'sent' && target === 'draft') return 'This will clear sent_at.';
    if (current === 'paid' && (target === 'sent' || target === 'draft')) return 'This will clear paid_at.';
    if (target === 'void') return 'The invoice will remain visible in the ledger.';
    return '';
}

export default class {
    static init = () => {
        list();
        form();
        detail();
        manage();
    }
}

// =============================================================================
// LIST — server-side DataTable with overdue toggle
// =============================================================================

function list(): void {
    const tableEl = document.querySelector('.datatable[data-table="invoices_list"]') as HTMLElement | null;
    if (!tableEl) return;

    const overdueToggle = document.querySelector('.js-overdue-toggle') as HTMLElement | null;

    const table = new DataTable('.datatable[data-table="invoices_list"]', {
        pageLength: 20,
        lengthMenu: [10, 20, 50, 100],
        processing: true,
        serverSide: true,
        ajax: {
            url: '?do=getInvoices',
            data: (d: any) => {
                if (overdueToggle && overdueToggle.classList.contains('active')) {
                    d.overdue = 1;
                }
                return d;
            },
        },
        columns: [
            // 0: invoice_number
            { data: 'invoice_number' },
            // 1: client_name
            { data: 'client_name' },
            // 2: invoice_date — format M j, Y
            {
                data: 'invoice_date',
                render: (data: string) => formatDateDisplay(data),
            },
            // 3: due_date — overdue rows get text-danger
            {
                data: null as any,
                render: (data: any) => {
                    const formatted = formatDateDisplay(data.due_date);
                    return data.is_overdue
                        ? `<span class="text-danger">${formatted}</span>`
                        : formatted;
                },
            },
            // 4: total_amount — $ + number_format(2), right-aligned tabular-nums
            {
                data: null as any,
                className: 'text-end',
                render: (data: any) =>
                    `<span class="font-variant-numeric-tabular">$${number_format(parseFloat(data.total_amount), 2)}</span>`,
            },
            // 5: status badge (+ overdue badge when applicable)
            {
                data: null as any,
                render: (data: any) => {
                    const cls = STATUS_BADGE[data.status] ?? 'bg-secondary';
                    let html = `<span class="badge ${cls}" aria-label="Status: ${data.status}">${capitalize(data.status)}</span>`;
                    if (data.is_overdue && (data.status === 'draft' || data.status === 'sent')) {
                        html += ` <span class="badge bg-danger ms-1" aria-label="Overdue">Overdue</span>`;
                    }
                    return html;
                },
            },
            // 6: actions — View / Edit / status-change dropdown
            {
                data: null as any,
                orderable: false,
                render: (data: any) => {
                    const id = data.id;
                    const num = data.invoice_number;
                    const status = data.status;
                    const statusOptions = ['draft', 'sent', 'paid', 'void']
                        .filter(s => s !== status)
                        .map(s => `<li><a class="dropdown-item js-status-change" href="#" data-id="${id}" data-invoice-number="${num}" data-status="${s}" data-current="${status}">${capitalize(s)}</a></li>`)
                        .join('');
                    return `<div class="d-flex gap-1 align-items-center">
                        <a href="/invoice/${id}" class="btn btn-outline-secondary btn-sm">View</a>
                        <a href="/invoice/${id}/edit" class="btn btn-outline-primary btn-sm">Edit</a>
                        <div class="dropdown">
                            <button class="btn btn-outline-secondary btn-sm dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false"></button>
                            <ul class="dropdown-menu dropdown-menu-end">${statusOptions}</ul>
                        </div>
                    </div>`;
                },
            },
        ],
        order: [[0, 'desc']] as Order[],
        createdRow: (row: Node, data: any) => {
            if (data.is_overdue) {
                (row as HTMLElement).classList.add('table-danger');
            }
        },
    });

    // Overdue toggle
    if (overdueToggle) {
        overdueToggle.addEventListener('click', () => {
            overdueToggle.classList.toggle('active');
            overdueToggle.classList.toggle('btn-warning');
            overdueToggle.classList.toggle('btn-outline-warning');
            table.ajax.reload();
        });
    }
}

// =============================================================================
// FORM — line-item editor, live totals, drag reorder, due-date auto-calc, submit
// =============================================================================

function form(): void {
    const tbody = document.querySelector('.js-line-items-tbody') as HTMLElement | null;
    if (!tbody) return;

    // Monotonic index for newly-added rows. The row template uses an __INDEX__
    // placeholder in its field names; without replacement every added row would
    // post under the same line_items[__INDEX__] key and PHP would keep only the
    // last one (CR-03). Server-rendered edit rows occupy 0..n-1, so start above
    // them and never reuse an index (distinct keys are all PHP needs).
    let nextLineIndex = tbody.querySelectorAll('tr').length;

    const template = document.getElementById('line-item-row-template') as HTMLTemplateElement | null;
    const addLineBtn = document.querySelector('.js-add-line') as HTMLElement | null;
    const taxRateInput = document.querySelector('[name="tax_rate"]') as HTMLInputElement | null;
    const emptyAlert = document.querySelector('.js-line-items-empty') as HTMLElement | null;
    const saveBtn = document.querySelector('.js-save-invoice') as HTMLButtonElement | null;
    const invoiceDateInput = document.querySelector('[name="invoice_date"]') as HTMLInputElement | null;
    const dueDateInput = document.querySelector('[name="due_date"]') as HTMLInputElement | null;
    const termsSelect = document.querySelector('[name="terms_days"]') as HTMLSelectElement | null;
    const deleteBtn = document.querySelector('.js-delete-invoice') as HTMLElement | null;

    // Populate reimbursement preset datalist if it exists in the DOM
    const datalist = document.getElementById('reimbursement-presets') as HTMLDataListElement | null;
    if (datalist && datalist.options.length === 0) {
        REIMBURSEMENT_PRESETS.forEach(preset => {
            const opt = document.createElement('option');
            opt.value = preset;
            datalist.appendChild(opt);
        });
    }

    // -- SortableJS drag reorder (D-02) --
    new Sortable(tbody, {
        handle: '.js-drag-handle',
        animation: 150,
        ghostClass: 'table-active',
        onEnd: updateSortOrder,
    });

    // -- Add line row --
    if (addLineBtn && template) {
        addLineBtn.addEventListener('click', () => {
            const clone = template.content.cloneNode(true) as DocumentFragment;
            tbody.appendChild(clone);
            const newRow = tbody.lastElementChild as HTMLElement;
            // Replace the __INDEX__ placeholder in every field name with a unique
            // index so this row posts under its own line_items[N] key (CR-03).
            newRow.querySelectorAll('[name]').forEach(el => {
                const name = el.getAttribute('name');
                if (name) el.setAttribute('name', name.replace(/__INDEX__/g, String(nextLineIndex)));
            });
            nextLineIndex++;
            // Default to flat
            const typeSelect = newRow.querySelector('.js-line-type') as HTMLSelectElement | null;
            if (typeSelect) typeSelect.value = 'flat';
            updateRowVisibility(newRow);
            updateSortOrder();
            recalculate();
            // Focus description input
            const descInput = newRow.querySelector('.js-line-title, .js-line-description') as HTMLElement | null;
            if (descInput) descInput.focus();
        });
    }

    // -- Remove line row (delegated) --
    document.addEventListener('click', (e: Event) => {
        const target = e.target as HTMLElement;
        if (!target.closest('.js-remove-line')) return;
        if (!tbody.contains(target)) return;
        const tr = target.closest('tr');
        if (tr) {
            tr.remove();
            updateSortOrder();
            recalculate();
        }
    });

    // -- Line-type change (delegated) --
    document.addEventListener('change', (e: Event) => {
        const target = e.target as HTMLElement;
        if (!target.classList.contains('js-line-type')) return;
        if (!tbody.contains(target)) return;
        const tr = target.closest('tr') as HTMLElement | null;
        if (tr) updateRowVisibility(tr);
        recalculate();
    });

    // -- Live totals (D-05): fire on any input in tbody or tax_rate change --
    tbody.addEventListener('input', () => recalculate());
    if (taxRateInput) {
        taxRateInput.addEventListener('change', () => recalculate());
        taxRateInput.addEventListener('input', () => recalculate());
    }

    // -- Due-date auto-calc (D-06) --
    if (invoiceDateInput) {
        // flatpickr onChange via native change event on the hidden/text input
        invoiceDateInput.addEventListener('change', () => autoCalcDueDate());
    }
    if (termsSelect) {
        termsSelect.addEventListener('change', () => autoCalcDueDate());
    }

    // -- Form submit --
    const formEl = tbody.closest('form') as HTMLFormElement | null;
    if (formEl) {
        formEl.addEventListener('submit', (e: Event) => {
            e.preventDefault();

            // Pre-submit validation
            const rows = tbody.querySelectorAll('tr');
            if (rows.length === 0) {
                if (emptyAlert) emptyAlert.style.display = '';
                return;
            }
            if (emptyAlert) emptyAlert.style.display = 'none';

            if (saveBtn) saveBtn.disabled = true;

            const data = new FormData(formEl);

            fetch('?do=saveInvoice', {
                method: 'POST',
                body: data,
            })
            .then(r => r.json())
            .then((res: any) => {
                if (res.success) {
                    window.location.href = res.redirect;
                } else {
                    showFlash(res.message ?? 'Could not save invoice. Please check the form and try again.', 'danger');
                    if (saveBtn) saveBtn.disabled = false;
                }
            })
            .catch(() => {
                showFlash('An error occurred saving the invoice. Please try again.', 'danger');
                if (saveBtn) saveBtn.disabled = false;
            });
        });
    }

    // -- Delete invoice (D-16) --
    if (deleteBtn) {
        deleteBtn.addEventListener('click', (e: Event) => {
            e.preventDefault();
            const id = deleteBtn.getAttribute('data-id') ?? '';
            const num = deleteBtn.getAttribute('data-invoice-number') ?? '';
            showDeleteModal(id, num);
        });
    }

    // Run initial recalculate for existing rows (edit page)
    tbody.querySelectorAll('tr').forEach(tr => updateRowVisibility(tr as HTMLElement));
    recalculate();

    // -------------------------------------------------------------------------
    // Helpers
    // -------------------------------------------------------------------------

    function updateRowVisibility(tr: HTMLElement): void {
        const typeSelect = tr.querySelector('.js-line-type') as HTMLSelectElement | null;
        if (!typeSelect) return;
        const lineType = typeSelect.value;

        // Column groups — hide/show per line-type matrix (UI-SPEC)
        const descriptionCell = tr.querySelector('.js-col-description') as HTMLElement | null;
        const hoursCell       = tr.querySelector('.js-col-hours')       as HTMLElement | null;
        const rateCell        = tr.querySelector('.js-col-rate')        as HTMLElement | null;
        const amountCell      = tr.querySelector('.js-col-amount')      as HTMLElement | null;
        const rateInput       = tr.querySelector('.js-line-rate')       as HTMLInputElement | null;
        const amountInput     = tr.querySelector('.js-line-amount')     as HTMLInputElement | null;

        if (lineType === 'hours') {
            if (descriptionCell) descriptionCell.style.display = 'none';
            if (hoursCell)       hoursCell.style.display       = '';
            if (rateCell)        rateCell.style.display        = '';
            if (amountCell)      amountCell.style.display      = '';
            // Prefill rate $30.00 if empty (UI-SPEC)
            if (rateInput && (!rateInput.value || rateInput.value === '0' || rateInput.value === '0.00')) {
                rateInput.value = '30.00';
            }
            // Amount is computed read-only for hours
            if (amountInput) {
                amountInput.readOnly = true;
                amountInput.classList.add('bg-secondary');
            }
        } else if (lineType === 'reimbursement') {
            if (descriptionCell) descriptionCell.style.display = '';
            if (hoursCell)       hoursCell.style.display       = 'none';
            if (rateCell)        rateCell.style.display        = 'none';
            if (amountCell)      amountCell.style.display      = '';
            if (rateInput)       rateInput.value               = '';
            if (amountInput) {
                amountInput.readOnly = false;
                amountInput.classList.remove('bg-secondary');
            }
        } else {
            // flat (default)
            if (descriptionCell) descriptionCell.style.display = '';
            if (hoursCell)       hoursCell.style.display       = 'none';
            if (rateCell)        rateCell.style.display        = 'none';
            if (amountCell)      amountCell.style.display      = '';
            if (rateInput)       rateInput.value               = '';
            if (amountInput) {
                amountInput.readOnly = false;
                amountInput.classList.remove('bg-secondary');
            }
        }
    }

    function recalculate(): void {
        const rows = tbody!.querySelectorAll('tr');
        let subtotal = 0;

        rows.forEach(tr => {
            const typeSelect  = tr.querySelector('.js-line-type')   as HTMLSelectElement | null;
            const hoursInput  = tr.querySelector('.js-line-hours')  as HTMLInputElement  | null;
            const rateInput   = tr.querySelector('.js-line-rate')   as HTMLInputElement  | null;
            const amountInput = tr.querySelector('.js-line-amount') as HTMLInputElement  | null;

            if (!typeSelect || !amountInput) return;

            const lineType = typeSelect.value;

            if (lineType === 'hours') {
                const hours = parseFloat(hoursInput?.value ?? '0') || 0;
                const rate  = parseFloat(rateInput?.value  ?? '0') || 0;
                const amt   = Math.round(hours * rate * 100) / 100;
                amountInput.value = amt.toFixed(2);
                subtotal += amt;
            } else {
                subtotal += parseFloat(amountInput.value) || 0;
            }
        });

        subtotal = Math.round(subtotal * 100) / 100;

        const taxRate = parseFloat(taxRateInput?.value ?? '13') || 0;
        // Tax rate entered as percentage (e.g. 13 = 13%) — convert to decimal
        const taxDecimal = taxRate > 1 ? taxRate / 100 : taxRate;
        const taxAmount  = Math.round(subtotal * taxDecimal * 100) / 100;
        const total      = Math.round((subtotal + taxAmount) * 100) / 100;

        const subtotalEl  = document.getElementById('js-subtotal');
        const taxAmountEl = document.getElementById('js-tax-amount');
        const totalEl     = document.getElementById('js-total');

        if (subtotalEl)  subtotalEl.textContent  = '$' + number_format(subtotal, 2);
        if (taxAmountEl) taxAmountEl.textContent  = '$' + number_format(taxAmount, 2);
        if (totalEl)     totalEl.textContent      = '$' + number_format(total, 2);
    }

    function updateSortOrder(): void {
        const rows = tbody!.querySelectorAll('tr');
        rows.forEach((tr, i) => {
            const sortInput = tr.querySelector('.js-sort-order') as HTMLInputElement | null;
            if (sortInput) sortInput.value = String(i);
        });
    }

    function autoCalcDueDate(): void {
        if (!invoiceDateInput || !termsSelect || !dueDateInput) return;
        const dateStr = invoiceDateInput.value;
        if (!dateStr) return;
        const terms = parseInt(termsSelect.value, 10) || 0;
        const invoiceDate = new Date(dateStr);
        if (isNaN(invoiceDate.getTime())) return;
        invoiceDate.setDate(invoiceDate.getDate() + terms);
        const yyyy = invoiceDate.getFullYear();
        const mm   = String(invoiceDate.getMonth() + 1).padStart(2, '0');
        const dd   = String(invoiceDate.getDate()).padStart(2, '0');
        const newDate = `${yyyy}-${mm}-${dd}`;
        // Update flatpickr instance if available, else set value directly
        const fpInstance = (dueDateInput as any)._flatpickr;
        if (fpInstance) {
            fpInstance.setDate(newDate, false);
        } else {
            dueDateInput.value = newDate;
        }
    }
}

// =============================================================================
// DETAIL + STATUS CONFIRM — shared between list rows and detail page
// =============================================================================

function detail(): void {
    // Wire status-change dropdown items (delegated — works for both list DataTable rows
    // and the static detail page dropdown)
    document.addEventListener('click', (e: Event) => {
        const target = e.target as HTMLElement;
        const item = target.closest('.js-status-change') as HTMLElement | null;
        if (!item) return;
        e.preventDefault();

        const id            = item.getAttribute('data-id') ?? '';
        const invoiceNumber = item.getAttribute('data-invoice-number') ?? '';
        const status        = item.getAttribute('data-status') ?? '';
        const current       = item.getAttribute('data-current') ?? '';

        showStatusModal(id, invoiceNumber, status, current);
    });

    // Generate PDF (CR-01): the trigger is a button, not a GET anchor, so generation
    // (a Spaces PUT + pdf_path UPDATE) can't be forged or prefetched. Delegated so it
    // works on the static detail page.
    document.addEventListener('click', (e: Event) => {
        const target = e.target as HTMLElement;
        const btn = target.closest('.js-generate-pdf') as HTMLButtonElement | null;
        if (!btn) return;
        e.preventDefault();
        doGeneratePdf(btn);
    });

    // Confirm button in status modal
    const confirmBtn = document.querySelector('.js-status-confirm-btn') as HTMLElement | null;
    if (confirmBtn) {
        confirmBtn.addEventListener('click', () => {
            const id     = confirmBtn.getAttribute('data-id')     ?? '';
            const status = confirmBtn.getAttribute('data-status') ?? '';
            if (!id || !status) return;
            doChangeStatus(parseInt(id, 10), status);
        });
    }
}

function showStatusModal(id: string, invoiceNumber: string, targetStatus: string, currentStatus: string): void {
    const modal        = document.getElementById('invoice-status-modal');
    const numEl        = modal?.querySelector('.js-status-inv-num');
    const badgeEl      = modal?.querySelector('.js-status-target-badge');
    const noteEl       = modal?.querySelector('.js-status-timestamp-note');
    const confirmBtn   = modal?.querySelector('.js-status-confirm-btn') as HTMLElement | null;

    if (!modal) return;

    if (numEl)      numEl.textContent      = invoiceNumber;
    if (noteEl)     noteEl.textContent     = statusTimestampNote(targetStatus, currentStatus);

    const badgeClass = STATUS_BADGE[targetStatus] ?? 'bg-secondary';
    if (badgeEl) {
        badgeEl.className = `badge ${badgeClass}`;
        badgeEl.textContent = capitalize(targetStatus);
    }

    if (confirmBtn) {
        confirmBtn.setAttribute('data-id', id);
        confirmBtn.setAttribute('data-status', targetStatus);
        // Reset button classes then apply target status class
        confirmBtn.className = `btn btn-sm js-status-confirm-btn ${STATUS_BTN[targetStatus] ?? 'btn-secondary'}`;
    }

    const bsModal = (window as any).bootstrap?.Modal?.getOrCreateInstance(modal);
    if (bsModal) bsModal.show();
}

function doChangeStatus(id: number, status: string): void {
    fetch(`?do=changeStatus&id=${id}&status=${status}`, {method: 'POST'})
        .then(r => r.json())
        .then((res: any) => {
            const modal = document.getElementById('invoice-status-modal');
            const bsModal = (window as any).bootstrap?.Modal?.getInstance(modal);
            if (bsModal) bsModal.hide();

            if (res.success) {
                showFlash(`Status updated to ${status}.`, 'success');
                // Update badge in DOM on detail page (if present)
                const detailBadge = document.querySelector('.js-invoice-status-badge') as HTMLElement | null;
                if (detailBadge) {
                    const cls = STATUS_BADGE[status] ?? 'bg-secondary';
                    detailBadge.className = `badge ${cls} js-invoice-status-badge`;
                    detailBadge.textContent = capitalize(status);
                }
                // Reload DataTable if on list page
                const tableEl = document.querySelector('.datatable[data-table="invoices_list"]');
                if (tableEl) {
                    const dtInstance = (DataTable as any).tables({ visible: true, api: true });
                    if (dtInstance) dtInstance.ajax.reload(null, false);
                }
            } else {
                showFlash(res.message ?? 'Could not update status.', 'danger');
            }
        })
        .catch(() => showFlash('An error occurred. Please try again.', 'danger'));
}

function doGeneratePdf(btn: HTMLButtonElement): void {
    const id     = btn.getAttribute('data-id') ?? '';
    const pdfUrl = btn.getAttribute('data-pdf-url') ?? '';
    if (!id) return;

    const originalText = btn.textContent ?? 'Generate PDF';
    btn.disabled = true;
    btn.textContent = 'Generating…';
    const restore = () => { btn.disabled = false; btn.textContent = originalText; };

    fetch(`?do=generatePdf&id=${id}`, {method: 'POST'})
        .then(r => r.json())
        .then((res: any) => {
            if (!res.success) {
                showFlash(res.message ?? 'PDF generation failed. Please try again.', 'danger');
                restore();
                return;
            }
            const url = res.redirect ?? pdfUrl;
            // Swap Generate → Download in place so the now-stale button can't
            // re-trigger generation (WR-04).
            const control = btn.closest('.js-pdf-control');
            if (control && url) {
                control.innerHTML =
                    `<a href="${url}" class="btn btn-outline-success btn-sm" target="_blank">Download PDF</a>`;
            } else {
                restore();
            }
            // Try to open the stored PDF immediately; if the popup is blocked (this
            // runs in an async callback, outside the click gesture) the freshly-swapped
            // Download button is the visible fallback.
            const opened = url ? window.open(url, '_blank') : null;
            showFlash(opened ? 'PDF generated.' : 'PDF generated — click Download PDF to open.', 'success');
        })
        .catch(() => {
            showFlash('An error occurred. Please try again.', 'danger');
            restore();
        });
}

function showDeleteModal(id: string, invoiceNumber: string): void {
    const modal = document.getElementById('invoice-delete-modal');
    if (!modal) return;

    const bodyEl      = modal.querySelector('.js-delete-modal-body');
    const confirmBtn  = modal.querySelector('.js-delete-confirm-btn') as HTMLElement | null;

    if (bodyEl) {
        bodyEl.innerHTML = `Soft-delete <strong>${invoiceNumber}</strong>? This is recoverable — the invoice will be hidden from all views but the data is not lost.`;
    }

    if (confirmBtn) {
        confirmBtn.setAttribute('data-id', id);
        // Detach previous listener via clone trick
        const newBtn = confirmBtn.cloneNode(true) as HTMLElement;
        confirmBtn.parentNode?.replaceChild(newBtn, confirmBtn);
        newBtn.addEventListener('click', () => {
            doDeleteInvoice(parseInt(id, 10));
        });
    }

    const bsModal = (window as any).bootstrap?.Modal?.getOrCreateInstance(modal);
    if (bsModal) bsModal.show();
}

function doDeleteInvoice(id: number): void {
    fetch(`?do=deleteInvoice&id=${id}`, {method: 'POST'})
        .then(r => r.json())
        .then((res: any) => {
            const modal = document.getElementById('invoice-delete-modal');
            const bsModal = (window as any).bootstrap?.Modal?.getInstance(modal);
            if (bsModal) bsModal.hide();

            if (res.success) {
                showFlash('Invoice soft-deleted. Data is recoverable.', 'info');
                setTimeout(() => { window.location.href = '/invoice'; }, 1200);
            } else {
                showFlash('Could not delete invoice. Please try again.', 'danger');
            }
        })
        .catch(() => showFlash('An error occurred. Please try again.', 'danger'));
}

// =============================================================================
// MANAGE — client & sender add/edit forms (AJAX submit to handle* signals)
// =============================================================================

function manage(): void {
    // Each tuple: [form element id, Nette signal]. The forms render on the
    // client/sender add+edit pages; without this wiring a native POST would hit
    // the GET route and silently save nothing (CR-04 / INV-02).
    const forms: Array<[string, string]> = [
        ['js-client-form', '?do=saveClient'],
        ['js-sender-form', '?do=saveSender'],
    ];

    for (const [formId, signal] of forms) {
        const formEl = document.getElementById(formId) as HTMLFormElement | null;
        if (!formEl) continue;

        formEl.addEventListener('submit', (e: Event) => {
            e.preventDefault();
            const submitBtn = formEl.querySelector('[type="submit"]') as HTMLButtonElement | null;
            if (submitBtn) submitBtn.disabled = true;

            fetch(signal, {method: 'POST', body: new FormData(formEl)})
                .then(r => r.json())
                .then((res: any) => {
                    if (res.success) {
                        window.location.href = res.redirect ?? '/invoice';
                    } else {
                        showFlash(res.message ?? 'Could not save. Please check the form and try again.', 'danger');
                        if (submitBtn) submitBtn.disabled = false;
                    }
                })
                .catch(() => {
                    showFlash('An error occurred. Please try again.', 'danger');
                    if (submitBtn) submitBtn.disabled = false;
                });
        });
    }
}

// =============================================================================
// Shared utilities
// =============================================================================

function capitalize(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
}

/** Format a YYYY-MM-DD date string as "Jun 11, 2026" */
function formatDateDisplay(dateStr: string): string {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-').map(Number);
    const d = new Date(year, month - 1, day);
    return d.toLocaleDateString('en-CA', {year: 'numeric', month: 'short', day: 'numeric'});
}

function showFlash(message: string, type: 'success' | 'danger' | 'info' | 'warning'): void {
    // Append a Bootstrap alert to the top of main content (or body fallback)
    const container = document.querySelector('main .container, .container-fluid, main') as HTMLElement
        ?? document.body;
    const div = document.createElement('div');
    div.className = `alert alert-${type} alert-dismissible fade show mt-2`;
    div.setAttribute('role', 'alert');
    div.innerHTML = `${message} <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>`;
    container.prepend(div);
    // Auto-dismiss after 5s
    setTimeout(() => {
        div.classList.remove('show');
        setTimeout(() => div.remove(), 300);
    }, 5000);
}
